/**
 * After the RDS ⇄ Supabase two-way sync merged or replaced a clinic note:
 * tell the collab server (backend/api/collab/views.py) so a room that is
 * open RIGHT NOW takes the new note instead of showing -- and re-saving --
 * the old one. Anyone with the chart open sees the merged note land in
 * their editor as an ordinary remote change.
 *
 * Only clinic notes qualify (New/MedADocs/[schema/]…/<name>-<id>.json in
 * the files bucket); every other file is left to the sync alone.
 */
import { getBackendURL } from "../components/env";
import { getSessionToken } from "./rdsPhi";

const NOTE_PATH = /^New\/MedADocs\/(?:([A-Za-z_][A-Za-z0-9_]*)\/)?(?:\d{4}\/\d{4}-\d{2}-\d{2}|Patients)\/[^/]+\.json$/;

/** True for a clinic-note .json in the files bucket. */
export function isClinicNote(bucket, path) {
  return bucket === "files" && NOTE_PATH.test(String(path || ""));
}

/** `.../Foo-1.yjs` -> `.../Foo-1.json` (the note a collab snapshot belongs
 * to); null for anything that is not a `.yjs`. */
export function yjsSiblingOf(path) {
  const p = String(path || "");
  return p.toLowerCase().endsWith(".yjs") ? `${p.slice(0, -4)}.json` : null;
}

/** The tenant schema a note path is filed under ("public" when none);
 * null when the path is not a clinic note. */
export function schemaFromNotePath(path) {
  const m = NOTE_PATH.exec(String(path || ""));
  if (!m) return null;
  return m[1] || "public";
}

export class CollabRefreshError extends Error {
  constructor(status, detail) {
    super(detail || `collab server returned ${status}`);
    this.name = "CollabRefreshError";
    this.status = status;
  }
}

/**
 * POST /api/collab/refresh-document/ {path, bucket, document?}.
 * `document` is the merged envelope when the panel built it (merge); left
 * out when the sync copied a file whole -- the server then reads storage
 * and hands the document back (`document`). The server also pushes the
 * note into the other deployments' rooms (`peers`) and records the
 * "Version N Migrated" history entry in both stores (`version`).
 *
 * Resolves to {ok, path, schema, room: "live"|"idle"|"none", editors, users,
 * peers: [{peer, ok, error?}], version: {name, number, backup_path, stores} | null,
 * document?, json_rewritten?, error?} -- `ok: false` + `error` when any
 * part did not work (the rest still happened).
 * @throws {CollabRefreshError} no session, unreachable, or a non-2xx answer.
 */
/** Where the panel's backend (the collab server) is for this page. */
export function collabRefreshUrl() {
  return `${getBackendURL()}/api/collab/refresh-document/`;
}

const RETRY_AFTER_MS = 1500;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function refreshLiveDocument({
  path, bucket = "files", document, timeoutMs = 30000, retries = 1, retryAfterMs = RETRY_AFTER_MS,
} = {}) {
  if (!path) throw new Error("path is required");
  const token = await getSessionToken();
  if (!token) throw new CollabRefreshError(401, "no active session");
  const url = collabRefreshUrl();
  const payload = JSON.stringify({ path, bucket, ...(document ? { document } : {}) });
  for (let attempt = 0; ; attempt += 1) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      let resp;
      try {
        resp = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: payload,
          signal: controller.signal,
        });
      } catch (err) {
        if (err?.name === "AbortError") throw new CollabRefreshError(0, `collab server timed out (${url})`);
        // "Failed to fetch": no answer at all -- the server is down or
        // restarting, the page cannot reach that address, or something in
        // the browser blocked the call. One more try covers a restart.
        if (attempt < retries) {
          clearTimeout(timer);
          await sleep(retryAfterMs);
          continue;
        }
        throw new CollabRefreshError(0, `collab server unreachable at ${url}: ${err.message} (is the backend running there, and reachable from this page?)`);
      }
      let body = null;
      try { body = await resp.json(); } catch { body = null; }
      if (!resp.ok) throw new CollabRefreshError(resp.status, body?.error || `collab server returned ${resp.status}`);
      return body || {};
    } finally {
      clearTimeout(timer);
    }
  }
}

/**
 * Can this page reach the collab server at all? A GET on the refresh route
 * answers 405 ("POST required") when the server is up and on the right
 * code; 404 means an older backend; no answer means down or unreachable.
 * Resolves to {ok, url, reason?}; never throws. Asked before Apply so the
 * operator learns it BEFORE the sync, not from the log after it.
 */
export async function collabServerReachable({ timeoutMs = 8000 } = {}) {
  const url = collabRefreshUrl();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const resp = await fetch(url, { method: "GET", signal: controller.signal, cache: "no-store" });
    if (resp.status === 405) return { ok: true, url };
    if (resp.status === 404) return { ok: false, url, reason: "the backend there is older code without the refresh route (restart it on this branch)" };
    return { ok: false, url, reason: `the backend answered ${resp.status}` };
  } catch (err) {
    if (err?.name === "AbortError") return { ok: false, url, reason: "no answer in time" };
    return { ok: false, url, reason: `${err.message} (is the backend running there, and reachable from this page?)` };
  } finally {
    clearTimeout(timer);
  }
}

function hostOf(url) {
  try { return new URL(url).host; } catch { return String(url); }
}

/** One line for the sync log about what the refresh did, here and on
 * each peer deployment. */
export function describeRefresh(res) {
  if (!res) return "";
  const parts = [];
  if (res.room === "live") {
    const n = Number(res.editors || 0);
    parts.push(`${n} open editor${n === 1 ? "" : "s"} updated`);
  } else if (res.room === "idle") parts.push("updated in memory (nobody had it open)");
  else parts.push("nobody had it open here");
  (res.peers || []).forEach((p) => {
    parts.push(`${hostOf(p.peer)}: ${p.ok ? "updated" : "NOT updated"}`);
  });
  return parts.join(" · ");
}
